import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { deleteFee } from '../../API';
import { getCurrency, getShortDate, toggleOptionsMenu } from '../../Helper';
import YesNoModal from '../YesNoModal';

function FeeTable(props) {
    const [deleteFeeId, setDeleteFeeId] = useState(null);
    const [deletionConfirmationModal, setDeletionConfirmationModal] = useState(false);

    const toggleDeletionConfirmationModal = () => setDeletionConfirmationModal(!deletionConfirmationModal);
    const deleteRecord = async () => {
        if (deleteFeeId)
            await props.deleteRecord(deleteFeeId);
    };
    const cancelDelete = () => {
        setDeleteFeeId(null);
    };
    const setupDelete = (feeId) => {
        setDeleteFeeId(feeId);
        toggleDeletionConfirmationModal();
    };

    if (props.fees === null)
        props.fees = [];

    const defaultOptions = {
        fields: {
            accountNumber: true,
            account: true,
            division: true,
            facility: true,
            identifier: true,
            description: true,
            fiscalYear: true,
            amount: true,
            balance: true,
            dueDate: true,
            paidInFull: true,
            actions: true
        },
        footer: {
            amountTotal: true,
            balanceTotal: true
        },
        actions: {
            add: false,
            view: true,
            edit: true,
            delete: true,
            viewAccount: true
        }
    }
    const options = { ...defaultOptions, ...props.options };

    const tableBody = props.fees.sort((a, b) => b.dueDate - a.dueDate).map(f => {
        const viewURL = '/admin/fees/' + f.feeID;
        const editURL = '/admin/fees/' + f.feeID + '/edit';
        const paidInFullIcon = f.isPaidInFull ? (<i className="fas fa-check-square"></i>) : (<i className="far fa-square"></i>);
        return (
            <tr key={f.feeID}>
                {options.fields.accountNumber ? (
                    <td>
                        <span>{f.account.accountNumber}</span>
                    </td>
                ) : null}
                {options.fields.account ? (
                    <td>
                        <span>{f.account.name}</span>
                    </td>
                ) : null}
                {options.fields.division ? (
                    <td>
                        <span>{f.division?.divisionName}</span>
                    </td>
                ) : null}
                {options.fields.facility ? (
                    <td>
                        <span>{f.facility}</span>
                    </td>
                ) : null}
                {options.fields.identifier ? (
                    <td>
                        <span>{f.identifier}</span>
                    </td>
                ) : null}
                {options.fields.description ? (
                    <td>
                        <span>{f.feeDescription}</span>
                    </td>
                ) : null}
                {options.fields.fiscalYear ? (
                    <td>
                        <span>{f.fiscalYear}</span>
                    </td>
                ) : null}
                {options.fields.amount ? (
                    <td>
                        <span>{getCurrency(f.feeAmount)}</span>
                    </td>
                ) : null}
                {options.fields.balance ? (
                    <td>
                        <span>{getCurrency(f.currentBalance)}</span>
                    </td>
                ) : null}
                {options.fields.dueDate ? (
                    <td>
                        <span>{getShortDate(f.dueDate)}</span>
                    </td>
                ) : null}
                {options.fields.paidInFull ? (
                    <td className="center">
                        <span>{paidInFullIcon}</span>
                    </td>
                ) : null}
                {options.fields.actions ? (
                    <td>
                        <div className="actions">
                            {options.actions.view ? (
                                <Link to={viewURL} className="action-icon action-view">
                                    <i className="fas fa-eye"></i>
                                </Link>
                            ) : null}
                            {options.actions.edit && window.env.user.userRoles.some(ur => [1, 2].includes(ur.roleID)) ? (
                                <Link to={editURL} className="action-icon action-edit">
                                    <i className="fas fa-edit"></i>
                                </Link>
                            ) : null}
                            {options.actions.viewAccount || (options.actions.delete && window.env.user.userRoles.some(ur => [1, 2].includes(ur.roleID))) ? (
                                <div onClick={toggleOptionsMenu} className="action-icon action-dropdown">
                                    <i className="fas fa-ellipsis-h"></i>
                                    <div className="action-dropdown-menu">
                                        {options.actions.viewAccount ? (
                                            <Link className="action-dropdown-menu-item" to={`/admin/accounts/${f.accountID}`}>View Account</Link>
                                        ) : null}
                                        {options.actions.delete && window.env.user.userRoles.some(ur => [1, 2].includes(ur.roleID)) ? (
                                            <div className="action-dropdown-menu-item" onClick={() => setupDelete(f.feeID)}>Delete</div>
                                        ) : null}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </td>
                ) : null}
            </tr>
        );
    });

    const totalAmount = props.fees.map(f => isNaN(f.feeAmount) ? 0 : f.feeAmount).reduce((a, b) => a + b, 0);
    const totalPaid = props.fees.map(f => isNaN(f.paid) ? 0 : f.paid).reduce((a, b) => a + b, 0);
    const totalBalance = props.fees.map(f => isNaN(f.currentBalance) ? 0 : f.currentBalance).reduce((a, b) => a + b, 0);
    const totalPaidInFull = props.fees.every(f => f.isPaidInFull) ? (<i className="fas fa-check-square"></i>) : (<i className="far fa-square" ></i>);

    return (
        <div>
            <table className="table">
                <thead>
                    <tr>
                        {options.fields.accountNumber ? (
                            <th>
                                <span>Account Number</span>
                            </th>
                        ) : null}
                        {options.fields.account ? (
                            <th>
                                <span>Account</span>
                            </th>
                        ) : null}
                        {options.fields.division ? (
                            <th>
                                <span>Program</span>
                            </th>
                        ) : null}
                        {options.fields.facility ? (
                            <th>
                                <span>Facility</span>
                            </th>
                        ) : null}
                        {options.fields.identifier ? (
                            <th>
                                <span>EPA ID/Permit #</span>
                            </th>
                        ) : null}
                        {options.fields.description ? (
                            <th>
                                <span>Description</span>
                            </th>
                        ) : null}
                        {options.fields.fiscalYear ? (
                            <th>
                                <span>Fiscal Year</span>
                            </th>
                        ) : null}
                        {options.fields.amount ? (
                            <th>
                                <span>Fee Amount</span>
                            </th>
                        ) : null}
                        {options.fields.balance ? (
                            <th>
                                <span>Balance Due</span>
                            </th>
                        ) : null}
                        {options.fields.dueDate ? (
                            <th>
                                <span>Due Date</span>
                            </th>
                        ) : null}
                        {options.fields.paidInFull ? (
                            <th>
                                <span>Paid In Full</span>
                            </th>
                        ) : null}
                        {options.fields.actions ? (
                            <th>
                                {options.actions.add && window.env.user.userRoles.some(ur => [1, 2].includes(ur.roleID)) ? (
                                    <div className="actions">
                                        <Link to={`/admin/accounts/${options.accountId}/addfee`} className="action-icon action-add float-right tooltip-parent">
                                            <i className="fas fa-plus"></i>
                                            <div className="tooltip-target">
                                                Add Fee
                                            </div>
                                        </Link>
                                    </div>
                                ) : null}
                            </th>
                        ) : null}
                    </tr>
                </thead>
                <tbody>
                    {tableBody}
                </tbody>
                {Object.values(options.footer).some(v => v) ? (
                    <tfoot>
                        <tr>
                            {options.fields.accountNumber ? (
                                <td>
                                </td>
                            ) : null}
                            {options.fields.account ? (
                                <td>
                                </td>
                            ) : null}
                            {options.fields.division ? (
                                <td>
                                </td>
                            ) : null}
                            {options.fields.facility ? (
                                <td>
                                </td>
                            ) : null}
                            {options.fields.identifier ? (
                                <td>
                                </td>
                            ) : null}
                            {options.fields.description ? (
                                <td>
                                </td>
                            ) : null}
                            {options.fields.fiscalYear ? (
                                <td>
                                </td>
                            ) : null}
                            {options.fields.amount ? (
                                <td>
                                    {options.footer.amountTotal ? (<span>{getCurrency(totalAmount)}</span>) : null}
                                </td>
                            ) : null}
                            {options.fields.balance ? (
                                <td>
                                    {options.footer.balanceTotal ? (<span>{getCurrency(totalBalance)}</span>) : null}
                                </td>
                            ) : null}
                            {options.fields.dueDate ? (
                                <td>
                                </td>
                            ) : null}
                            {options.fields.paidInFull ? (
                                <td>
                                    <span>{totalPaidInFull}</span>
                                </td>
                            ) : null}
                            {options.fields.actions ? (
                                <td>
                                </td>
                            ) : null}
                        </tr>
                    </tfoot>
                ) : null}
            </table>
            <YesNoModal title="Confirm Deletion" yesClick={deleteRecord} noClick={cancelDelete} open={deletionConfirmationModal} toggle={toggleDeletionConfirmationModal}>Are you sure you want delete this Fee?</YesNoModal>
        </div>


        //<div className="fee-table table">
        //    <div className="table-header">
        //        <div className="table-row">
        //            <div className="table-cell center">
        //                <span>Facility</span>
        //            </div>
        //            <div className="table-cell center">
        //                <span>Permit #</span>
        //            </div>
        //            <div className="table-cell center">
        //                <span>Description</span>
        //            </div>
        //            <div className="table-cell center">
        //                <span>Amount</span>
        //            </div>
        //            <div className="table-cell center">
        //                <span>Paid</span>
        //            </div>
        //            <div className="table-cell center">
        //                <span>Due</span>
        //            </div>
        //            <div className="table-cell center">
        //                <span>Due Date</span>
        //            </div>
        //            <div className="table-cell center">
        //                <span>Program</span>
        //            </div>
        //            <div className="table-cell center">
        //                <span>Paid In Full</span>
        //            </div>
        //        </div>
        //    </div>

        //    <div className="table-body">
        //        {tableBody}
        //    </div>

        //    <div className="table-footer">
        //    </div>
        //</div>
    );
}

export default FeeTable;